// MarketResearch — a scroll-first, Gen-Z friendly digest of open-source
// market intelligence (DataReportal + curated stat cards). Every card is
// react-able, note-able, save-able and shareable so the surface earns
// XP the same way News + Learning do.
//
// Design intent
// -------------
// * Cards feel like Instagram carousels + Twitter numbers, not like a
//   McKinsey slide deck. Big numbers, punchy tags, one-line takeaway.
// * Filter chips (kind / category / region) live on top as pill rows so
//   they feel like TikTok "for you" tabs, not a legacy dropdown filter.
// * Right-rail (desktop) surfaces the user's own saves + a "why this
//   matters" primer so first-timers understand the value in <10s.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Sparkles,
    TrendingUp,
    TrendingDown,
    Bookmark,
    BookmarkCheck,
    Share2,
    MessageCircle,
    ExternalLink,
    Loader2,
    Search,
    RefreshCw,
    X,
    Send,
    Trash2,
    Filter,
    Zap,
    Globe2,
    BarChart3,
    Clock,
    Link2,
    Linkedin,
    Mail,
} from "lucide-react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    LineChart,
    Line,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

// ------- helpers -------
const REACTIONS = ["🔥", "💡", "🎯", "🤩", "👀", "❤️"];

const compactNumber = (n, unit = "") => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    const num = Number(n);
    if (unit === "%" || unit === "hours") {
        return `${Number.isInteger(num) ? num : num.toFixed(1)}${unit === "%" ? "%" : ""}`;
    }
    const abs = Math.abs(num);
    if (abs >= 1e9) return `${(num / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
    if (abs >= 1e6) return `${(num / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
    if (abs >= 1e3) return `${(num / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
    return `${num}`;
};

const relTime = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

// Compose a short share message. The `source_url` is what receivers see
// a rich preview of when pasted into WhatsApp/Slack/LinkedIn — the OG
// crawlers on those apps hit the DataReportal / publisher URL directly.
const composeShare = (item) => {
    const title = item.title;
    const summary = item.blurb || "";
    const number = item.kind === "stat"
        ? `${compactNumber(item.big_number, item.unit)}${item.unit && item.unit !== "%" ? ` ${item.unit}` : ""}`
        : "";
    const text = number
        ? `${title} — ${number}\n${summary}`
        : `${title}\n${summary}`;
    const url = item.source_url || (typeof window !== "undefined" ? window.location.href : "");
    return { title, text, url, composed: `${text}\n\nvia ${item.source || "Odyssey"}\n${url}` };
};

// ------- Share dialog (mirrors News/ShareDialog UX) -------
const ShareDialog = ({ item, onClose }) => {
    const dialogRef = useRef(null);
    const { title, text, url, composed } = useMemo(() => composeShare(item), [item]);

    useEffect(() => {
        const onDoc = (e) => {
            if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose?.();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const intents = [
        {
            key: "whatsapp",
            label: "WhatsApp",
            href: `https://wa.me/?text=${encodeURIComponent(composed)}`,
            Icon: MessageCircle,
            tint: "bg-emerald-50/70 text-emerald-700 ring-emerald-200/70 hover:bg-emerald-100/80",
        },
        {
            key: "linkedin",
            label: "LinkedIn",
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            Icon: Linkedin,
            tint: "bg-sky-50/70 text-sky-700 ring-sky-200/70 hover:bg-sky-100/80",
        },
        {
            key: "x",
            label: "X (Twitter)",
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
            Icon: null,
            tint: "bg-slate-900/90 text-white ring-slate-900/40 hover:bg-slate-900",
        },
        {
            key: "email",
            label: "Email",
            href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
            Icon: Mail,
            tint: "bg-rose-50/70 text-rose-700 ring-rose-200/70 hover:bg-rose-100/80",
        },
    ];

    const handleCopy = async () => {
        const ok = await copyToClipboard(url);
        if (ok) toast.success("Source link copied");
        else {
            try { window.prompt("Copy this link:", url); }
            catch { toast.error("Couldn't copy — try right-click → Copy"); }
        }
        onClose?.();
    };

    const handleNativeShare = async () => {
        if (!navigator.share) return;
        try {
            await navigator.share({ title, text, url });
            onClose?.();
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Share failed");
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-950/25 backdrop-blur-md"
            data-testid="mr-share-dialog"
        >
            <div ref={dialogRef} className="relative w-full max-w-lg animate-[mr-share-pop_180ms_ease-out]">
                <div className="relative bg-white/30 backdrop-blur-3xl backdrop-saturate-200 rounded-2xl border border-white/50 ring-1 ring-white/30 overflow-hidden shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)]">
                    <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
                    <span aria-hidden className="pointer-events-none absolute -top-1/2 -left-1/2 h-full w-full rounded-full bg-white/20 blur-3xl" />
                    <div className="relative px-6 pt-5 pb-4 border-b border-white/40">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F7F79]">
                                <Share2 className="h-3.5 w-3.5" /> Share insight
                            </span>
                            <button onClick={onClose} aria-label="Close" data-testid="mr-share-close-btn" className="text-slate-600 hover:text-slate-900 -mr-1">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 truncate">
                            {item.source}{item.ingested_at && <span className="text-slate-500 font-normal normal-case tracking-normal"> · {relTime(item.ingested_at)}</span>}
                        </div>
                        <div className="text-base font-bold text-slate-900 line-clamp-2 mt-1.5 leading-snug">{title}</div>
                    </div>
                    <div className="relative p-6 space-y-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 mb-2">Share link</div>
                            <div className="flex items-center gap-2 p-2 pl-3 bg-white/30 backdrop-blur-md border border-white/50 rounded-lg ring-1 ring-white/30">
                                <Link2 className="h-4 w-4 text-[#2EB4AC] shrink-0" />
                                <span className="text-xs font-mono text-slate-800 truncate flex-1" title={url}>{url}</span>
                                <button
                                    onClick={handleCopy}
                                    data-testid="mr-share-copy"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#2EB4AC] hover:bg-[#25908A] text-white text-xs font-bold shadow-[0_4px_12px_-2px_rgba(46,180,172,0.5)]"
                                >Copy</button>
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 mb-2">Share to</div>
                            <div className="grid grid-cols-4 gap-2.5">
                                {intents.map(({ key, label, href, Icon, tint }) => (
                                    <a
                                        key={key}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`mr-share-${key}`}
                                        onClick={() => onClose()}
                                        className={`flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-xl ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${tint}`}
                                    >
                                        {Icon ? <Icon className="h-5 w-5" /> : (
                                            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                                                <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.84l-5.36-7.06L4.6 22H1.34l8.05-9.19L1 2h6.84l4.86 6.43L18.244 2Zm-1.2 18h1.88L7.07 4H5.12l11.924 16Z" />
                                            </svg>
                                        )}
                                        <span className="text-[10px] font-semibold uppercase tracking-[0.08em]">{label}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                        {typeof navigator !== "undefined" && navigator.share && (
                            <button
                                onClick={handleNativeShare}
                                data-testid="mr-share-native"
                                className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-white/30 hover:bg-white/50 backdrop-blur-md border border-white/50 ring-1 ring-white/30 text-slate-800 text-sm font-semibold"
                            >
                                <Share2 className="h-4 w-4" /> More apps…
                            </button>
                        )}
                        <p className="text-[11px] text-slate-600 text-center pt-1">
                            Preview links resolve to the original DataReportal / publisher page.
                        </p>
                    </div>
                </div>
            </div>
            <style>{`@keyframes mr-share-pop { from { opacity: 0; transform: scale(0.96) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }`}</style>
        </div>,
        document.body
    );
};

// ------- Delta pill -------
const DeltaPill = ({ pct, label }) => {
    if (pct === null || pct === undefined) return null;
    const up = Number(pct) >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`}
            data-testid="mr-delta-pill"
        >
            <Icon className="h-3 w-3" />
            {up ? "+" : ""}
            {Number(pct).toFixed(1)}% <span className="opacity-70">{label || "YoY"}</span>
        </span>
    );
};

// ------- Reaction rail -------
const ReactionRail = ({ counts, mine, onReact, busy }) => (
    <div className="flex items-center gap-1" data-testid="mr-reaction-rail">
        {REACTIONS.map((e) => {
            const c = counts?.[e] || 0;
            const picked = mine?.includes(e);
            return (
                <button
                    key={e}
                    disabled={busy}
                    onClick={() => onReact(e)}
                    data-testid={`mr-react-${e}`}
                    className={`px-2 h-7 rounded-full text-xs font-medium transition-all border ${
                        picked
                            ? "bg-teal-50 border-teal-300 text-teal-800 shadow-sm"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                >
                    <span className="mr-1">{e}</span>
                    {c > 0 && <span className="tabular-nums">{c}</span>}
                </button>
            );
        })}
    </div>
);

// ------- Note thread modal -------
const NoteThread = ({ item, onClose, onCountChange }) => {
    const { user } = useAuth();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [posting, setPosting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/research/${item.id}/notes`);
            setNotes(r.data || []);
            onCountChange?.(item.id, (r.data || []).length);
        } catch {
            toast.error("Couldn't load notes");
        } finally {
            setLoading(false);
        }
    }, [item.id, onCountChange]);

    useEffect(() => { load(); }, [load]);

    // Esc to close
    useEffect(() => {
        const h = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    const post = async () => {
        const t = text.trim();
        if (!t) return;
        setPosting(true);
        try {
            const r = await api.post(`/research/${item.id}/notes`, { text: t });
            setNotes((prev) => [r.data, ...prev]);
            onCountChange?.(item.id, notes.length + 1);
            setText("");
            toast.success("Note added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't post note");
        } finally {
            setPosting(false);
        }
    };

    const del = async (nid) => {
        try {
            await api.delete(`/research/notes/${nid}`);
            setNotes((prev) => prev.filter((n) => n.id !== nid));
            onCountChange?.(item.id, notes.length - 1);
            toast.success("Note deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete");
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="mr-note-thread"
        >
            <div
                className="bg-white w-full max-w-lg rounded-2xl shadow-2xl max-h-[85vh] flex flex-col animate-[mr-share-pop_180ms_ease-out]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-slate-100">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-teal-600 font-semibold">Community notes</div>
                        <div className="mt-0.5 font-heading font-semibold text-slate-900 truncate">{item.title}</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-3 p-1.5 rounded-lg hover:bg-slate-100"
                        data-testid="mr-note-close-btn"
                    >
                        <X className="h-4 w-4 text-slate-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {loading ? (
                        <div className="flex items-center justify-center py-10 text-slate-400">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading notes…
                        </div>
                    ) : notes.length === 0 ? (
                        <div className="text-center py-10 text-slate-500 text-sm">
                            <MessageCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            No notes yet — kick off the conversation.
                        </div>
                    ) : (
                        notes.map((n) => (
                            <div key={n.id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`mr-note-${n.id}`}>
                                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                                    <div className="font-medium text-slate-700">{n.author_name}</div>
                                    <div className="flex items-center gap-2">
                                        <span>{relTime(n.created_at)}</span>
                                        {(n.is_mine || user?.role === "super_admin") && (
                                            <button
                                                onClick={() => del(n.id)}
                                                className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                                                data-testid={`mr-note-delete-${n.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
                            </div>
                        ))
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 bg-white">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post();
                            }}
                            placeholder="Add a take — what does this mean for us?"
                            className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none px-3 py-2 text-sm min-h-[42px] max-h-32"
                            rows={1}
                            data-testid="mr-note-input"
                        />
                        <button
                            onClick={post}
                            disabled={!text.trim() || posting}
                            className="h-10 px-4 rounded-xl bg-[#2EB4AC] hover:bg-[#25908A] disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors"
                            data-testid="mr-note-submit-btn"
                        >
                            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Post
                        </button>
                    </div>
                    <div className="mt-1.5 text-[10px] text-slate-400">
                        ⌘/Ctrl + Enter to post · Be respectful, be sharp.
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const _WORLD_POP = 8_100_000_000; // ~8.1B end-2025
const _INDIA_POP = 1_470_000_000; // ~1.47B end-2026 (UN medium projection)

// Turn a raw number into a visceral one-liner. Reused by the card
// footer + the detail dialog's Numbers-in-Context panel so both places
// stay in sync — one place to tweak the physical framing.
// Map each stat category to a magazine-style theme — a bespoke identity
// per card kind so the deck reads like an editorial series, not a
// dashboard. Grad = full-bleed hero gradient. Deep = solid-band colour.
// Emoji = a small motif in the corner tag.
const _THEMES = {
    Digital:            { grad: "from-[#0B4F4C] via-[#1F7F79] to-[#2EB4AC]", deep: "bg-[#0F3D3B]", accent: "text-[#5FE2DA]", motif: "🌐" },
    Social:             { grad: "from-[#4C1D95] via-[#7C3AED] to-[#EC4899]", deep: "bg-[#3B0764]", accent: "text-fuchsia-200",motif: "◎" },
    Content:            { grad: "from-[#7C2D12] via-[#EA580C] to-[#F59E0B]", deep: "bg-[#431407]", accent: "text-amber-200",  motif: "▶" },
    Mobile:             { grad: "from-[#0C4A6E] via-[#0284C7] to-[#38BDF8]", deep: "bg-[#082F49]", accent: "text-sky-200",    motif: "◈" },
    "E-commerce":       { grad: "from-[#78350F] via-[#D97706] to-[#F59E0B]", deep: "bg-[#451A03]", accent: "text-amber-200",  motif: "◐" },
    "Country digital":  { grad: "from-[#831843] via-[#DB2777] to-[#F472B6]", deep: "bg-[#500724]", accent: "text-rose-200",   motif: "◇" },
    Behaviour:          { grad: "from-[#312E81] via-[#4F46E5] to-[#818CF8]", deep: "bg-[#1E1B4B]", accent: "text-indigo-200", motif: "◈" },
    "Quick commerce":   { grad: "from-[#064E3B] via-[#059669] to-[#34D399]", deep: "bg-[#022C22]", accent: "text-emerald-200",motif: "⚡" },
};
const _DEFAULT_THEME = _THEMES.Digital;
const magazineTheme = (item) => _THEMES[item?.category] || _DEFAULT_THEME;

// ------- Curious question — reshapes a dry stat title into an
// intriguing, hook-y one-liner so the flashcard front reads like a
// pub-quiz question rather than a spreadsheet header.
//
// The rules are pattern-based on title tokens + category, so new stats
// scraped from DataReportal automatically get a decent question without
// hand-authoring per card.
const curiousQuestion = (item) => {
    const t = String(item?.title || "").trim();
    if (!t) return "Take a guess.";
    if (t.endsWith("?")) return t; // already a question

    // "X users in Y" → "How many X does Y have?"
    let m = /^(.+?\susers?)\sin\s(.+)$/i.exec(t);
    if (m) return `How many ${m[1].toLowerCase()} does ${m[2]} have?`;

    // "X advertising reach"
    m = /^(.+?)\s+advertising\s+reach$/i.exec(t);
    if (m) return `How many humans can ${m[1]} put an ad in front of?`;

    // "Time X spend online / day"
    if (/Time\s+.*\s+spend\s+online/i.test(t)) {
        return "How many hours a day does the average Indian actually spend online?";
    }

    // "Smartphone penetration"
    if (/smartphone\s+penetration/i.test(t)) {
        return "What share of Indian adults now own a smartphone?";
    }

    // "YouTube reach in X"
    m = /^(YouTube|Instagram|TikTok|Facebook|LinkedIn|Snapchat|X|Twitter|Pinterest)\s+reach\s+in\s+(.+?)(?:\s+\(.*\))?$/i.exec(t);
    if (m) return `What % of ${m[2].replace(/\s*age\s*\d+\+.*/i, "")} adults does ${m[1]} reach?`;

    // "Quick-commerce user base" / "user base"
    if (/quick[- ]commerce/i.test(t)) return "How many Indians are ordering groceries in under 15 minutes?";

    // GMV / e-commerce
    if (/e[- ]?commerce\s+GMV/i.test(t) || /GMV/i.test(t)) return "How big is India's e-commerce market this year?";

    // Country deep-dive stats — "Internet/Social/Mobile connections in <Country>"
    m = /^(Internet|Social media|Mobile)\s+(users|connections|identities?)\s+in\s+(.+)$/i.exec(t);
    if (m) return `How many ${m[1].toLowerCase()} ${m[2].toLowerCase()} does ${m[3]} have today?`;

    // Fallback — soften and question-ify.
    return `Care to guess: ${t.toLowerCase()}?`;
};

// A small rotating pre-hook line to add curiosity on top of the question.
// Deterministic per item so it doesn't churn on every render.
const _CURIOUS_HOOKS = [
    "No Googling — take your best shot.",
    "Trust your gut on this one.",
    "Bet you can't nail this cold.",
    "Quick — what's your guess?",
    "Care to venture a number?",
    "Ready? Pick a range.",
];
const curiousHook = (item) => {
    const s = String(item?.id || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h * 31 + s.charCodeAt(i)) | 0) >>> 0;
    return _CURIOUS_HOOKS[h % _CURIOUS_HOOKS.length];
};

const microComparison = (item) => {
    const big = Number(item?.big_number) || 0;
    if (big < 1_000_000) return "";
    // World-population fraction reads best for the *huge* numbers
    // (platform reach, global internet). Skip for small country stats.
    if (big >= _WORLD_POP * 0.1) {
        const pct = (big / _WORLD_POP) * 100;
        return `≈ ${pct.toFixed(1)}% of everyone on Earth`;
    }
    // "Stadiums" for planet-scale numbers → 100k capacity.
    if (big >= 5_000_000) {
        const stadiums = Math.round(big / 100_000);
        if (stadiums >= 10 && stadiums <= 200_000) {
            return `≈ ${stadiums.toLocaleString("en-IN")} sold-out Wembley Stadiums`;
        }
    }
    // Fallback: fraction of India's population (relatable for our audience).
    if (big >= _INDIA_POP) {
        return `≈ ${(big / _INDIA_POP).toFixed(2)}× India's population`;
    }
    const pct = (big / _INDIA_POP) * 100;
    if (pct >= 1) return `≈ ${pct.toFixed(1)}% of India's population`;
    return "";
};

// ------- Mini sparkline preview (used in-card) -------
const MiniSparkline = ({ series }) => {
    const points = series?.points || [];
    if (points.length < 2) return null;
    const values = points.map((p) => Number(p.value) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const W = 100;
    const H = 28;
    const step = W / (values.length - 1);
    const coords = values.map((v, i) => [i * step, H - ((v - min) / range) * (H - 4) - 2]);
    const path = coords.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const areaPath = `${path} L${W},${H} L0,${H} Z`;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-7 mt-2" preserveAspectRatio="none" aria-hidden>
            <defs>
                <linearGradient id="mr-sparkline-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2EB4AC" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#2EB4AC" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#mr-sparkline-fill)" />
            <path d={path} fill="none" stroke="#1F7F79" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

// ------- Stat of the Day — a "Higher / Lower" style guessing game that
// rewards +3 XP for a correct guess (once per user per day).
//
// Design intent
// -------------
// * Picks a deterministic stat per day so every teammate is playing
//   the *same* daily question — turns it into a mini water-cooler moment.
// * Uses range buckets (Under / Around / Over) instead of numeric input
//   so it's a 2-tap game, not a keyboard chore.
// * Persists "played today" locally so we don't re-open the guess flow
//   after a page refresh.
const _dayHash = (str) => {
    const s = String(str || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h * 31 + s.charCodeAt(i)) | 0) >>> 0;
    return h;
};
const _todayKey = () => new Date().toISOString().slice(0, 10);
const _stopEvt = (e) => e.stopPropagation();

const StatOfTheDay = ({ items, onOpenDetail }) => {
    // Pick eligible stats (must have a real big_number to guess against).
    const eligible = useMemo(
        () => (items || []).filter((it) => it.kind === "stat" && Number(it.big_number) > 0),
        [items],
    );
    // Deterministic daily pick — hash today's date across the sorted id
    // list so the same stat lands for every user on the same day.
    const todaysItem = useMemo(() => {
        if (!eligible.length) return null;
        const idx = _dayHash(_todayKey()) % eligible.length;
        return eligible[idx];
    }, [eligible]);

    const storageKey = todaysItem ? `sotd:${todaysItem.id}:${_todayKey()}` : "";
    const [state, setState] = useState(() => {
        if (typeof window === "undefined" || !storageKey) return "guess";
        return window.localStorage.getItem(storageKey) || "guess"; // "guess" | "revealed"
    });
    const [pick, setPick] = useState("");
    const [xp, setXp] = useState(0);
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.localStorage.getItem(`sotd:dismissed:${_todayKey()}`) === "1";
    });

    // Yesterday's answer + roster — pulled once when the component mounts.
    // We show it below the guess/reveal card as social proof.
    const [yesterday, setYesterday] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/research/sotd/yesterday");
                if (!cancelled) setYesterday(r.data);
            } catch { /* silent — the ribbon is nice-to-have */ }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!todaysItem || dismissed) return null;

    // Build 3 buckets around the true value: low = < 0.5×, mid = 0.5×–2×, high = > 2×.
    const truth = Number(todaysItem.big_number);
    const low = truth * 0.5;
    const high = truth * 2;
    const buckets = [
        { key: "low", label: `Under ${compactNumber(low, todaysItem.unit)}`, hi: low },
        { key: "mid", label: `${compactNumber(low, todaysItem.unit)} – ${compactNumber(high, todaysItem.unit)}` },
        { key: "high", label: `Over ${compactNumber(high, todaysItem.unit)}`, lo: high },
    ];
    // Correct bucket is always "mid" by construction of the ranges above,
    // but keeping the branch explicit reads more clearly.
    const correctBucket = "mid";

    const play = async (bucketKey) => {
        setPick(bucketKey);
        const correct = bucketKey === correctBucket;
        let awarded = 0;
        try {
            const r = await api.post("/research/sotd/play", { item_id: todaysItem.id, correct });
            // Backend returns either a raw int or an award_xp result dict —
            // unwrap both shapes so the reveal reads the right number.
            const raw = r.data?.xp_awarded;
            awarded = typeof raw === "number" ? raw : Number(raw?.awarded || 0);
        } catch { /* non-blocking */ }
        setXp(awarded);
        setState("revealed");
        try { window.localStorage.setItem(storageKey, "revealed"); } catch { /* private mode */ }
        if (correct && awarded > 0) {
            toast.success(`+${awarded} XP — sharp instincts.`);
        } else if (correct) {
            toast.success("Nice one — already played today.");
        }
    };

    const dismiss = () => {
        setDismissed(true);
        try { window.localStorage.setItem(`sotd:dismissed:${_todayKey()}`, "1"); } catch { /* noop */ }
    };

    const bigStr = `${compactNumber(truth, todaysItem.unit)}${todaysItem.unit && todaysItem.unit !== "%" ? "" : ""}`;

    return (
        <section
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F3D3B] via-[#1F7F79] to-[#2EB4AC] text-white shadow-[0_20px_60px_-20px_rgba(15,61,59,0.5)] mb-5"
            data-testid="mr-sotd"
        >
            {/* Ambient glows */}
            <span aria-hidden className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <span aria-hidden className="absolute -bottom-10 -left-10 h-56 w-56 rounded-full bg-teal-200/10 blur-3xl" />
            {/* Dismiss */}
            <button
                onClick={dismiss}
                className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm inline-flex items-center justify-center transition-colors z-10"
                aria-label="Dismiss"
                data-testid="mr-sotd-dismiss"
            >
                <X className="h-4 w-4" />
            </button>

            <div className="relative p-5 sm:p-7">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/20 px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold">
                    <Sparkles className="h-3 w-3" /> Stat of the day · +3 XP for a correct guess
                </div>

                <h3 className="mt-2 font-heading font-bold text-xl sm:text-2xl leading-snug max-w-2xl">
                    {todaysItem.title}
                    <span className="text-teal-100/85"> — take your best guess.</span>
                </h3>

                {/* Category / region context */}
                <div className="mt-1 text-[11px] uppercase tracking-widest font-semibold text-teal-100/70">
                    {todaysItem.category} · {todaysItem.region} · Source: {todaysItem.source}
                </div>

                {/* Guess buckets */}
                {state === "guess" ? (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        {buckets.map((b) => (
                            <button
                                key={b.key}
                                onClick={() => play(b.key)}
                                className="group h-12 rounded-xl bg-white/10 hover:bg-white/25 border border-white/25 backdrop-blur-sm text-sm font-semibold transition-all hover:-translate-y-0.5"
                                data-testid={`mr-sotd-guess-${b.key}`}
                            >
                                {b.label}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="mt-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-teal-100/80 font-semibold">Actual figure</div>
                            <div className="mt-1 flex items-baseline gap-2">
                                <span className="font-heading font-bold text-4xl sm:text-5xl tabular-nums leading-none">{bigStr}</span>
                                {todaysItem.unit && todaysItem.unit !== "%" && (
                                    <span className="text-teal-100/85 text-sm font-medium">{todaysItem.unit}</span>
                                )}
                                {pick === correctBucket ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 text-emerald-100 text-xs font-bold px-2 py-0.5 ring-1 ring-emerald-300/40">
                                        Nailed it{xp ? ` · +${xp} XP` : ""}
                                    </span>
                                ) : pick ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-white/15 text-white text-xs font-bold px-2 py-0.5">
                                        Not quite — try tomorrow
                                    </span>
                                ) : null}
                            </div>
                            <div className="mt-1.5 text-sm text-teal-50/90 max-w-xl">{todaysItem.blurb}</div>
                        </div>
                        <button
                            onClick={(e) => { _stopEvt(e); onOpenDetail(todaysItem); }}
                            className="shrink-0 h-10 px-4 rounded-xl bg-white text-slate-900 hover:bg-teal-50 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors self-start sm:self-auto"
                            data-testid="mr-sotd-details-btn"
                        >
                            See the full story
                        </button>
                    </div>
                )}

                {state === "guess" && (
                    <div className="mt-3 text-[11px] text-teal-100/70">
                        A new question drops every 24 hours. Everyone in your team gets the same one.
                    </div>
                )}
            </div>
            {/* Yesterday's answer ribbon — social proof + FOMO */}
            {yesterday?.item && (
                <div
                    className="relative border-t border-white/15 bg-black/25 backdrop-blur-sm px-5 sm:px-7 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
                    data-testid="mr-sotd-yesterday"
                >
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-teal-100/80 font-semibold">
                        <Clock className="h-3 w-3" /> Yesterday
                    </span>
                    <div className="flex items-baseline gap-2 min-w-0 flex-1">
                        <span className="text-sm font-semibold text-white truncate">
                            {yesterday.item.title}
                        </span>
                        <span className="font-mono font-bold text-white tabular-nums shrink-0">
                            {compactNumber(yesterday.item.big_number, yesterday.item.unit)}
                            {yesterday.item.unit && yesterday.item.unit !== "%" ? "" : ""}
                        </span>
                    </div>
                    {yesterday.correct_players.length > 0 ? (
                        <div className="inline-flex items-center gap-1.5" data-testid="mr-sotd-yesterday-roster">
                            <div className="flex -space-x-1.5">
                                {yesterday.correct_players.slice(0, 5).map((p) => (
                                    <span
                                        key={p.user_id}
                                        title={p.name}
                                        className="h-6 w-6 rounded-full ring-2 ring-[#0F3D3B] bg-teal-400/40 text-white text-[10px] font-bold inline-flex items-center justify-center overflow-hidden"
                                        style={p.picture ? { backgroundImage: `url(${p.picture})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                                    >
                                        {!p.picture && (p.name?.[0] || "?").toUpperCase()}
                                    </span>
                                ))}
                            </div>
                            <span className="text-[11px] text-teal-100/85 font-medium">
                                {yesterday.correct_players.length === 1
                                    ? `${yesterday.correct_players[0].name.split(" ")[0]} nailed it`
                                    : `${yesterday.correct_players.length} teammates nailed it`}
                                {yesterday.total_players > yesterday.correct_players.length && (
                                    <span className="text-teal-100/60"> · {yesterday.total_players - yesterday.correct_players.length} missed</span>
                                )}
                            </span>
                        </div>
                    ) : (
                        <span className="text-[11px] text-teal-100/70">
                            Nobody played yesterday — be the first to break the streak today.
                        </span>
                    )}
                </div>
            )}
        </section>
    );
};

// ------- Stat card -------
const StatCard = ({ item, saved, onToggleSave, onReact, onOpenNotes, onOpenDetail, onShare, savingId, reactingId }) => {
    const busySave = savingId === item.id;
    const busyReact = reactingId === item.id;
    const number = item.kind === "stat"
        ? `${compactNumber(item.big_number, item.unit)}${item.unit && item.unit !== "%" ? "" : ""}`
        : null;
    const hasSeries = Array.isArray(item.series?.points) && item.series.points.length >= 2;

    // The card body is clickable → opens detail. But inner interactive
    // controls (reaction pills, save/share/notes buttons, source link)
    // stopPropagation so click-through doesn't fire.
    const stop = (e) => e.stopPropagation();

    return (
        <article
            onClick={() => onOpenDetail(item)}
            className={`group relative bg-white rounded-2xl border p-5 flex flex-col transition-all hover:shadow-lg hover:-translate-y-0.5 cursor-pointer ${
                item.is_featured
                    ? "border-teal-200 shadow-[0_1px_0_rgba(46,180,172,0.08)] ring-1 ring-teal-100/50"
                    : "border-slate-200"
            }`}
            data-testid={`mr-card-${item.id}`}
        >
            {(item.is_editors_pick || item.is_featured) && (
                <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 shadow">
                    <Sparkles className="h-3 w-3" />
                    {item.is_editors_pick ? "Editor's pick" : "Trending"}
                </div>
            )}

            {/* header row: category + region */}
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">
                <span className="inline-flex items-center gap-1">
                    <BarChart3 className="h-3 w-3" />
                    {item.category || "Insight"}
                </span>
                <span className="inline-flex items-center gap-1">
                    <Globe2 className="h-3 w-3" />
                    {item.region || "Global"}
                </span>
            </div>

            {/* big number */}
            {number && (
                <div className="mb-1 flex items-baseline gap-2">
                    <div className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 tracking-tight tabular-nums leading-none">
                        {number}
                    </div>
                    {item.unit && item.unit !== "%" && (
                        <div className="text-sm text-slate-500 font-medium">{item.unit}</div>
                    )}
                </div>
            )}
            {hasSeries && <MiniSparkline series={item.series} />}

            {/* title + blurb */}
            <div className="mt-2 font-heading font-semibold text-slate-900 text-base leading-snug">{item.title}</div>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.blurb}</p>

            {/* delta + tags */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {item.delta_pct !== undefined && item.delta_pct !== null && (
                    <DeltaPill pct={item.delta_pct} label={item.delta_label} />
                )}
                {(item.tags || []).slice(0, 3).map((t) => (
                    <span
                        key={t}
                        className="inline-block rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-0.5"
                    >
                        #{t}
                    </span>
                ))}
            </div>

            {/* Micro-comparison — one visceral one-liner deriving from
                the big number. Skipped when the number is small or when
                no useful framing exists. */}
            {(() => {
                const cmp = microComparison(item);
                if (!cmp) return null;
                return (
                    <div
                        className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1 self-start"
                        data-testid={`mr-microcmp-${item.id}`}
                    >
                        <Sparkles className="h-3 w-3 text-teal-500" />
                        <span>{cmp}</span>
                    </div>
                );
            })()}

            {/* source + timestamp */}
            <div className="mt-3 flex items-center justify-between gap-2 text-[11px]" onClick={stop}>
                <a
                    href={item.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-teal-700 truncate min-w-0"
                    data-testid={`mr-source-link-${item.id}`}
                >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{item.source}</span>
                </a>
                {item.ingested_at && (
                    <span
                        className="inline-flex items-center gap-1 text-slate-400 shrink-0"
                        title={new Date(item.ingested_at).toLocaleString("en-IN")}
                        data-testid={`mr-timestamp-${item.id}`}
                    >
                        <Clock className="h-3 w-3" />
                        {relTime(item.ingested_at)}
                    </span>
                )}
            </div>

            {/* footer: reactions + actions */}
            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2" onClick={stop}>
                <ReactionRail
                    counts={item.reaction_counts}
                    mine={item.my_reactions}
                    onReact={(e) => onReact(item, e)}
                    busy={busyReact}
                />
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onOpenNotes(item)}
                        className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center relative"
                        title="Community notes"
                        data-testid={`mr-notes-btn-${item.id}`}
                    >
                        <MessageCircle className="h-4 w-4" />
                        {item.note_count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-teal-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center tabular-nums">
                                {item.note_count}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => onShare(item)}
                        className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center"
                        title="Share"
                        data-testid={`mr-share-btn-${item.id}`}
                    >
                        <Share2 className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => onToggleSave(item)}
                        disabled={busySave}
                        className={`h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors ${
                            saved ? "bg-teal-50 text-teal-700" : "hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                        }`}
                        title={saved ? "Saved" : "Save"}
                        data-testid={`mr-save-btn-${item.id}`}
                    >
                        {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                    </button>
                </div>
            </div>
        </article>
    );
};

// ------- Report card (bigger cover) -------
const ReportCard = ({ item, saved, onToggleSave, onReact, onOpenNotes, onShare, savingId, reactingId }) => {
    const busySave = savingId === item.id;
    const busyReact = reactingId === item.id;
    return (
        <article
            className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-lg transition-all hover:-translate-y-0.5"
            data-testid={`mr-card-${item.id}`}
        >
            {item.cover_image ? (
                <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="block aspect-[16/9] bg-slate-100 overflow-hidden">
                    <img
                        src={item.cover_image}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                    />
                </a>
            ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-teal-50 via-slate-50 to-emerald-50 flex items-center justify-center">
                    <BarChart3 className="h-10 w-10 text-teal-300" />
                </div>
            )}
            <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">
                    {item.category || "Report"} · {item.region || "Global"}
                </div>
                <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="font-heading font-semibold text-slate-900 text-sm leading-snug hover:text-teal-700 line-clamp-3">
                    {item.title}
                </a>
                {item.blurb && (
                    <p className="mt-1.5 text-xs text-slate-600 line-clamp-2">{item.blurb}</p>
                )}
                <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                    <span className="truncate">{item.source}</span>
                    {(item.ingested_at || item.published_at) && (
                        <span
                            className="inline-flex items-center gap-1 text-slate-400 shrink-0"
                            title={new Date(item.ingested_at || item.published_at).toLocaleString("en-IN")}
                            data-testid={`mr-timestamp-${item.id}`}
                        >
                            <Clock className="h-3 w-3" />
                            {relTime(item.ingested_at || item.published_at)}
                        </span>
                    )}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <ReactionRail counts={item.reaction_counts} mine={item.my_reactions} onReact={(e) => onReact(item, e)} busy={busyReact} />
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => onOpenNotes(item)}
                            className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center"
                            title="Notes"
                            data-testid={`mr-notes-btn-${item.id}`}
                        >
                            <MessageCircle className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onShare(item)}
                            className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center"
                            title="Share"
                            data-testid={`mr-share-btn-${item.id}`}
                        >
                            <Share2 className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onToggleSave(item)}
                            disabled={busySave}
                            className={`h-8 w-8 rounded-full inline-flex items-center justify-center ${saved ? "bg-teal-50 text-teal-700" : "hover:bg-slate-100 text-slate-500"}`}
                            data-testid={`mr-save-btn-${item.id}`}
                        >
                            {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </article>
    );
};

// ------- Numbers in Context — shown in the detail dialog when a stat
// has no historical series. Turns a lone number into a mini-dashboard:
//   * A "reach" bar visualising the penetration % (auto-parsed from blurb)
//   * A grid of derived callouts (world/population comparison, YoY delta,
//     per-day rate, per-second rate — whichever apply)
//   * An "if that was a stadium" narrative comparison for kicks

// Try to pull a "X% of everyone on Earth / of the total population" from
// the blurb — we already surface this data via regex, but pre-extracting
// makes the callouts predictable.
const _parseContext = (item) => {
    const blurb = item.blurb || "";
    let pctOfWorld = null;
    let pctOfCountry = null;
    let asOf = "";

    const mWorld = /([\d.]+)\s*%\s*of\s+everyone\s+on\s+Earth/i.exec(blurb);
    if (mWorld) pctOfWorld = parseFloat(mWorld[1]);

    const mCountry = /([\d.]+)\s*%\s+of\s+population/i.exec(blurb)
        || /That['\u2019]s\s+([\d.]+)\s*%\s+of\s+(?:the\s+)?population/i.exec(blurb);
    if (mCountry) pctOfCountry = parseFloat(mCountry[1]);

    const mDate = /(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}/i.exec(blurb);
    if (mDate) asOf = mDate[0];
    else {
        const mYear = /\b(20\d{2})\b/.exec(blurb);
        if (mYear) asOf = mYear[0];
    }
    return { pctOfWorld, pctOfCountry, asOf };
};

const NumbersInContext = ({ item }) => {
    const { pctOfWorld, pctOfCountry, asOf } = _parseContext(item);
    const big = Number(item.big_number) || 0;
    const delta = item.delta_pct;

    // "If that was" — a fun physical/social comparison so the number
    // feels tangible. Only render when it lands sensibly.
    let comparison = null;
    if (big >= 100_000_000) {
        const stadiums = big / 100_000; // avg large stadium ≈ 100k
        if (stadiums >= 1000) {
            comparison = `That's ${Math.round(stadiums).toLocaleString("en-IN")} sold-out Wembley Stadiums.`;
        }
    }
    if (!comparison && big >= 1_000_000) {
        // Ratio to India's population (as of 2026 UN ≈ 1.47B) — always
        // a relatable frame for our Indian audience.
        const inPop = 1_470_000_000;
        if (big >= inPop) {
            comparison = `That's ${(big / inPop).toFixed(2)}× the entire population of India.`;
        } else if (big > inPop * 0.05) {
            comparison = `That's ${((big / inPop) * 100).toFixed(1)}% of India's population.`;
        }
    }

    // Growth per day / per second derived from YoY delta — makes an
    // annual number feel *alive* on the screen.
    let perDay = null;
    if (delta && big) {
        const yearlyAdd = big * (delta / 100);
        perDay = yearlyAdd / 365;
    }

    const worldPctForBar = pctOfWorld ?? (big && big < _WORLD_POP ? (big / _WORLD_POP) * 100 : null);

    return (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-teal-50/50 via-slate-50 to-white p-5 sm:p-6" data-testid="mr-detail-context">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
                Numbers in context {asOf && <span className="text-slate-400 font-normal normal-case tracking-normal">· as of {asOf}</span>}
            </div>

            {/* Reach bar — visualises penetration */}
            {worldPctForBar !== null && (
                <div className="mb-4" data-testid="mr-context-reach-bar">
                    <div className="flex items-baseline justify-between mb-1.5">
                        <span className="text-xs font-medium text-slate-600">Share of {pctOfCountry !== null && item.region !== "Global" ? item.region.toLowerCase() : "world"} population</span>
                        <span className="text-lg font-bold text-slate-900 tabular-nums">{(pctOfCountry ?? worldPctForBar).toFixed(1)}%</span>
                    </div>
                    <div className="relative h-2.5 rounded-full bg-slate-200 overflow-hidden">
                        <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] rounded-full transition-[width] duration-700"
                            style={{ width: `${Math.min(100, Math.max(0, pctOfCountry ?? worldPctForBar))}%` }}
                        />
                    </div>
                    <div className="mt-1 text-[10px] text-slate-400">
                        The remaining {(100 - (pctOfCountry ?? worldPctForBar)).toFixed(1)}% is greenfield addressable.
                    </div>
                </div>
            )}

            {/* Callout grid — up to 4 derived stat pills */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Headline</div>
                    <div className="mt-1 text-lg font-heading font-bold text-slate-900 tabular-nums leading-none">
                        {compactNumber(big, item.unit)}{item.unit && item.unit !== "%" ? "" : ""}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">{item.unit}</div>
                </div>
                {delta !== undefined && delta !== null && (
                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">YoY change</div>
                        <div className={`mt-1 text-lg font-heading font-bold tabular-nums leading-none ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {delta >= 0 ? "+" : ""}{Number(delta).toFixed(1)}%
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">vs last year</div>
                    </div>
                )}
                {pctOfWorld !== null && (
                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Global reach</div>
                        <div className="mt-1 text-lg font-heading font-bold text-slate-900 tabular-nums leading-none">{pctOfWorld}%</div>
                        <div className="text-[10px] text-slate-500 mt-1">of everyone on Earth</div>
                    </div>
                )}
                {perDay && (
                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Growth pace</div>
                        <div className="mt-1 text-lg font-heading font-bold text-teal-700 tabular-nums leading-none">
                            +{compactNumber(perDay)}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">per day</div>
                    </div>
                )}
            </div>

            {/* "If that was a stadium…" narrative — turns abstract into visceral */}
            {comparison && (
                <div className="mt-4 rounded-lg bg-white border border-teal-100 px-3 py-2.5 text-sm text-slate-700">
                    <span className="text-teal-600 font-semibold mr-1">→</span>
                    {comparison}
                </div>
            )}

            <div className="mt-4 text-[11px] text-slate-400">
                Historical series not published by the source. Open the DataReportal report for the underlying dataset and older figures.
            </div>
        </div>
    );
};


// ------- Stat detail dialog — full visualisation of the underlying series -------
const StatDetailDialog = ({ item, onClose, onOpenNotes, onShare, onToggleSave, onReact, saved }) => {
    const dialogRef = useRef(null);
    useEffect(() => {
        const onDoc = (e) => {
            if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose?.();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const series = item.series;
    const hasSeries = Array.isArray(series?.points) && series.points.length >= 2;
    const chartType = (series?.chart || "area").toLowerCase();
    const data = (series?.points || []).map((p) => ({ label: String(p.label), value: Number(p.value) }));

    const number = item.kind === "stat"
        ? `${compactNumber(item.big_number, item.unit)}${item.unit && item.unit !== "%" ? "" : ""}`
        : null;

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-md"
            data-testid="mr-detail-dialog"
        >
            <div ref={dialogRef} className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-[mr-share-pop_180ms_ease-out] bg-white rounded-2xl shadow-2xl">
                {/* Header — gradient teal band */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#0F3D3B] via-[#1F7F79] to-[#2EB4AC] text-white px-6 sm:px-8 pt-6 pb-5">
                    <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                    <div className="relative flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold">
                                <BarChart3 className="h-3 w-3" /> {item.category} · {item.region}
                            </div>
                            <h2 className="mt-2 font-heading font-bold text-xl sm:text-2xl leading-tight">{item.title}</h2>
                            {item.ingested_at && (
                                <div className="mt-1.5 flex items-center gap-1 text-[11px] text-teal-50/85">
                                    <Clock className="h-3 w-3" /> Ingested {relTime(item.ingested_at)}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            data-testid="mr-detail-close-btn"
                            className="shrink-0 rounded-full h-9 w-9 bg-white/15 hover:bg-white/25 backdrop-blur-sm inline-flex items-center justify-center transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="p-6 sm:p-8 space-y-6">
                    {/* Headline number + delta */}
                    {number && (
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <div className="font-heading font-bold text-5xl sm:text-6xl text-slate-900 tabular-nums leading-none">{number}</div>
                            {item.unit && item.unit !== "%" && <div className="text-slate-500 font-medium">{item.unit}</div>}
                            {item.delta_pct !== undefined && item.delta_pct !== null && (
                                <DeltaPill pct={item.delta_pct} label={item.delta_label} />
                            )}
                        </div>
                    )}
                    <p className="text-slate-700 leading-relaxed">{item.blurb}</p>

                    {/* CHART — the actual data visualisation */}
                    {hasSeries ? (
                        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 sm:p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                                    {series.unit || item.unit || "value"} · {data.length}-year trend
                                </div>
                                <div className="text-[10px] text-slate-400">Data: {item.source}</div>
                            </div>
                            <div className="w-full h-64 sm:h-72" data-testid="mr-detail-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    {chartType === "bar" ? (
                                        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                                            <defs>
                                                <linearGradient id="mr-bar-fill" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#2EB4AC" />
                                                    <stop offset="100%" stopColor="#0F3D3B" />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} width={50} />
                                            <Tooltip
                                                cursor={{ fill: "rgba(46,180,172,0.08)" }}
                                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                                formatter={(v) => [`${v}`, series.unit || item.unit]}
                                            />
                                            <Bar dataKey="value" fill="url(#mr-bar-fill)" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    ) : chartType === "line" ? (
                                        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} width={50} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                                formatter={(v) => [`${v}`, series.unit || item.unit]}
                                            />
                                            <Line type="monotone" dataKey="value" stroke="#1F7F79" strokeWidth={2.5} dot={{ r: 4, fill: "#2EB4AC", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    ) : (
                                        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                                            <defs>
                                                <linearGradient id="mr-area-fill" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.45} />
                                                    <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} width={50} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                                formatter={(v) => [`${v}`, series.unit || item.unit]}
                                            />
                                            <Area type="monotone" dataKey="value" stroke="#1F7F79" strokeWidth={2.5} fill="url(#mr-area-fill)" />
                                        </AreaChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                            {/* Data table below the chart — screen-readable + copy-friendly */}
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full text-xs" data-testid="mr-detail-table">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                                            <th className="py-1.5 pr-3 font-semibold">Year</th>
                                            <th className="py-1.5 pr-3 font-semibold">{series.unit || item.unit || "Value"}</th>
                                            <th className="py-1.5 font-semibold">Change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((row, i) => {
                                            const prev = i > 0 ? data[i - 1].value : null;
                                            const chg = prev ? ((row.value - prev) / prev) * 100 : null;
                                            return (
                                                <tr key={row.label} className="border-t border-slate-100">
                                                    <td className="py-1.5 pr-3 font-mono text-slate-700">{row.label}</td>
                                                    <td className="py-1.5 pr-3 font-mono text-slate-900 font-semibold tabular-nums">{row.value.toLocaleString("en-IN")}</td>
                                                    <td className={`py-1.5 font-mono tabular-nums ${chg === null ? "text-slate-400" : chg >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                                                        {chg === null ? "—" : `${chg >= 0 ? "+" : ""}${chg.toFixed(1)}%`}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <NumbersInContext item={item} />
                    )}

                    {/* Tags */}
                    {(item.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {item.tags.map((t) => (
                                <span key={t} className="inline-block rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium px-2.5 py-1">#{t}</span>
                            ))}
                        </div>
                    )}

                    {/* Source */}
                    <a
                        href={item.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 font-medium"
                        data-testid="mr-detail-source-link"
                    >
                        <ExternalLink className="h-4 w-4" />
                        View the full report on {new URL(item.source_url).host.replace(/^www\./, "")}
                    </a>

                    {/* Action rail — same actions as the card */}
                    <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <ReactionRail counts={item.reaction_counts} mine={item.my_reactions} onReact={(e) => onReact(item, e)} />
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { onOpenNotes(item); onClose(); }}
                                className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center gap-1.5"
                                data-testid="mr-detail-notes-btn"
                            >
                                <MessageCircle className="h-4 w-4" /> Notes
                            </button>
                            <button
                                onClick={() => onShare(item)}
                                className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center gap-1.5"
                                data-testid="mr-detail-share-btn"
                            >
                                <Share2 className="h-4 w-4" /> Share
                            </button>
                            <button
                                onClick={() => onToggleSave(item)}
                                className={`h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                                    saved ? "bg-teal-50 text-teal-800 border border-teal-200" : "border border-slate-200 hover:bg-slate-50 text-slate-700"
                                }`}
                                data-testid="mr-detail-save-btn"
                            >
                                {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                                {saved ? "Saved" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ------- Flashcard mode — one big card at a time with a guess-then-reveal
// interaction. The default landing UX per user's ask; grid stays reachable
// via the "Show all" toggle.
//
// Design intent
// -------------
// * ONE stat centred on screen — no scanning fatigue, no visual noise.
// * Every card starts as a *question* — 3 range buttons framed so the
//   correct answer is always the middle bucket (same trick as SOTD),
//   with a prominent teal REVEAL CTA for users who just want the number.
// * After reveal → the full stat card content unfolds: big number,
//   micro-comparison, blurb, historical chart (if any), source + timestamp.
// * Prev / Next arrows + keyboard L/R shortcuts, plus a progress bar so
//   users know how deep they are. Aced cards show a checkmark.
const _clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const FlashcardMode = ({
    items,
    savedIds,
    savingId,
    reactingId,
    canShowGrid,
    onSwitchToGrid,
    onOpenBoard,
    onReact,
    onToggleSave,
    onOpenNotes,
    onShare,
    onOpenDetail,
}) => {
    const [idx, setIdx] = useState(0);
    const [revealed, setRevealed] = useState(false);
    const [pick, setPick] = useState("");
    const [acedIds, setAcedIds] = useState([]);
    const [todayXp, setTodayXp] = useState(0);
    const [awarded, setAwarded] = useState(0);
    // Toggled true briefly on a correct answer to fire the celebration burst
    // (confetti-style rings + haptic + glow). Auto-clears after ~1.6s.
    const [celebrating, setCelebrating] = useState(false);
    // Toggled true briefly on a wrong answer — shakes the card, tints the
    // border rose, drops a puff of "dust" particles.
    const [wobbling, setWobbling] = useState(false);
    // Parallax tilt — track mouse position over the card and apply a 3D
    // rotation. Refs (not state) so we don't re-render 60 times/sec.
    const cardRef = useRef(null);
    const heroRef = useRef(null);
    const bodyRef = useRef(null);

    // Fetch the user's flashcard progress once — powers the aced badge
    // and the "+N XP today" counter in the header.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/research/flashcard/mine");
                if (!cancelled) {
                    setAcedIds(r.data?.aced_ids || []);
                    setTodayXp(r.data?.today_xp || 0);
                }
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Reset revealed state whenever the deck cursor moves.
    useEffect(() => {
        setRevealed(false);
        setPick("");
        setAwarded(0);
        setCelebrating(false);
        setWobbling(false);
    }, [idx]);

    const total = items.length;
    const item = items[idx];

    // Keyboard shortcuts — makes power-users blaze through the deck.
    // 1/2/3 answer the buckets, arrow keys navigate.
    useEffect(() => {
        const h = (e) => {
            if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
            if (e.key === "ArrowRight") { setIdx((i) => _clamp(i + 1, 0, total - 1)); }
            else if (e.key === "ArrowLeft") { setIdx((i) => _clamp(i - 1, 0, total - 1)); }
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [total]);

    if (!total || !item) {
        return (
            <div className="text-center py-24 text-slate-500 text-sm">
                No cards match your filters. <button className="underline text-teal-700" onClick={onSwitchToGrid}>Show all →</button>
            </div>
        );
    }

    const number = item.kind === "stat"
        ? `${compactNumber(item.big_number, item.unit)}`
        : null;
    const theme = magazineTheme(item);
    const hasSeries = Array.isArray(item.series?.points) && item.series.points.length >= 2;
    const truth = Number(item.big_number) || 0;
    const low = truth * 0.5;
    const high = truth * 2;
    const buckets = [
        { key: "low", label: `Under ${compactNumber(low, item.unit)}` },
        { key: "mid", label: `${compactNumber(low, item.unit)} – ${compactNumber(high, item.unit)}` },
        { key: "high", label: `Over ${compactNumber(high, item.unit)}` },
    ];
    const correctBucket = "mid";
    const aced = acedIds.includes(item.id);

    const answer = async (bucketKey) => {
        setPick(bucketKey);
        setRevealed(true);
        const correct = bucketKey === correctBucket;

        if (correct) {
            // Fire the celebration burst immediately — feels snappier than
            // waiting for the network round-trip. Reduced-motion users
            // get the toast + pill without the ring/glow.
            const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            if (!reduce) {
                setCelebrating(true);
                setTimeout(() => setCelebrating(false), 1600);
            }
            // Haptic buzz on mobile (safe no-op elsewhere).
            try { navigator.vibrate?.([12, 40, 12]); } catch { /* ignore */ }
        } else {
            // Wrong-answer feedback — viewport-level modal + subtle card
            // wobble + haptic. The modal is the primary signal so users
            // never miss it.
            const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            if (!reduce) {
                setWobbling(true);
                setTimeout(() => setWobbling(false), 900);
            }
            try { navigator.vibrate?.([30, 20, 30]); } catch { /* ignore */ }
        }

        try {
            const r = await api.post("/research/flashcard/answer", { item_id: item.id, correct });
            const raw = r.data?.xp_awarded;
            const n = typeof raw === "number" ? raw : Number(raw?.awarded || 0);
            setAwarded(n);
            if (correct && n > 0) {
                setAcedIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
                setTodayXp((v) => v + n);
                toast.success(`+${n} XP — nailed it.`);
            } else if (correct && !aced) {
                toast.success("Correct! (Already earned XP for this one.)");
            }
        } catch (e) {
            toast.error("Couldn't save answer");
        }
    };

    const prev = () => setIdx((i) => _clamp(i - 1, 0, total - 1));
    const next = () => setIdx((i) => _clamp(i + 1, 0, total - 1));

    // Parallax handlers — read cursor position vs card centre and drive a
    // 3D tilt on the article + a subtle counter-shift on the hero and
    // body layers for depth. Bypassed under prefers-reduced-motion.
    const _reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const onPointerMove = (e) => {
        if (_reduce || !cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;   // 0 → 1
        const y = (e.clientY - rect.top) / rect.height;   // 0 → 1
        const rx = (0.5 - y) * 6;   // tilt max ±3°
        const ry = (x - 0.5) * 8;   // tilt max ±4°
        cardRef.current.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
        if (heroRef.current) heroRef.current.style.transform = `translate3d(${(x - 0.5) * -10}px, ${(y - 0.5) * -6}px, 0)`;
        if (bodyRef.current) bodyRef.current.style.transform = `translate3d(${(x - 0.5) * 6}px, ${(y - 0.5) * 4}px, 0)`;
    };
    const onPointerLeave = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transform = "rotateX(0deg) rotateY(0deg)";
        if (heroRef.current) heroRef.current.style.transform = "translate3d(0,0,0)";
        if (bodyRef.current) bodyRef.current.style.transform = "translate3d(0,0,0)";
    };

    return (
        <div className="w-full" data-testid="mr-flashcard-mode" style={{ perspective: "1400px" }}>
            {/* Header — progress + XP counter + Show-all toggle */}
            <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="text-xs font-mono tabular-nums text-slate-500">
                        <span className="text-slate-900 font-bold">{idx + 1}</span>
                        <span className="mx-1">/</span>
                        <span>{total}</span>
                    </div>
                    <div className="flex-1 min-w-[80px] max-w-xs h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] rounded-full transition-[width] duration-300"
                            style={{ width: `${((idx + 1) / total) * 100}%` }}
                        />
                    </div>
                    {todayXp > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5" data-testid="mr-flashcard-xp-today">
                            <Sparkles className="h-3 w-3" /> +{todayXp} XP today
                        </span>
                    )}
                    {savedIds.length > 0 && (
                        <button
                            onClick={onOpenBoard}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-700 bg-white border border-slate-200 rounded-full px-2 py-0.5 hover:border-teal-400 hover:text-teal-800 transition-colors"
                            data-testid="mr-flashcard-board"
                            title="View your saved insights board"
                        >
                            <BookmarkCheck className="h-3 w-3 text-teal-600" /> Board ({savedIds.length})
                        </button>
                    )}
                </div>
                {canShowGrid && (
                    <button
                        onClick={onSwitchToGrid}
                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium"
                        data-testid="mr-flashcard-show-all"
                    >
                        <BarChart3 className="h-3.5 w-3.5" /> Show all
                    </button>
                )}
            </div>

            {/* Card — magazine-cover treatment: category-themed gradient hero
                band on top, editorial serif title, then a white body with the
                interactive parts. Border tints teal on correct / rose on wrong. */}
            <article
                key={item.id}
                ref={cardRef}
                onMouseMove={onPointerMove}
                onMouseLeave={onPointerLeave}
                className={`relative bg-white rounded-3xl border overflow-hidden animate-[mr-share-pop_260ms_ease-out] ${
                    celebrating
                        ? "border-teal-400 shadow-[0_0_0_6px_rgba(46,180,172,0.18),0_30px_80px_-25px_rgba(46,180,172,0.55)]"
                        : wobbling
                            ? "border-rose-400 shadow-[0_0_0_6px_rgba(244,63,94,0.15),0_30px_80px_-25px_rgba(244,63,94,0.4)]"
                            : "border-slate-200 shadow-[0_20px_60px_-25px_rgba(15,61,59,0.35)]"
                } ${wobbling ? "animate-[mr-wobble_500ms_ease-in-out]" : ""}`}
                // transform-style + will-change tell the browser to promote
                // this to its own compositor layer so tilt stays crisp.
                style={{
                    transformStyle: "preserve-3d",
                    willChange: "transform",
                    transition: "transform 300ms cubic-bezier(0.2, 0.9, 0.3, 1), box-shadow 300ms ease, border-color 300ms ease",
                }}
                data-testid={`mr-flashcard-${item.id}`}
            >
                {/* --- Magazine hero band (top ~40%) --- */}
                <div
                    ref={heroRef}
                    className={`relative overflow-hidden bg-gradient-to-br ${theme.grad} text-white`}
                    style={{ transition: "transform 300ms cubic-bezier(0.2, 0.9, 0.3, 1)", willChange: "transform" }}
                >
                    {/* Halftone dot pattern for editorial feel */}
                    <div
                        aria-hidden
                        className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
                        style={{
                            backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
                            backgroundSize: "14px 14px",
                        }}
                    />
                    <div aria-hidden className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/15 blur-3xl pointer-events-none" />
                    <div aria-hidden className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-black/25 blur-3xl pointer-events-none" />
                    <div className="relative px-6 sm:px-10 pt-6 pb-8">
                        {/* Corner tag — motif · region · issue N */}
                        <div className="flex items-center justify-between mb-6 text-[10px] uppercase tracking-[0.22em] font-bold">
                            <span className={`inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm px-2.5 py-0.5 ring-1 ring-white/20 ${theme.accent}`}>
                                <span aria-hidden>{theme.motif}</span>
                                {item.category || "Insight"}
                            </span>
                            <div className="flex items-center gap-2 text-white/80">
                                {aced && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/25 text-emerald-100 border border-emerald-300/40 px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal">
                                        <BookmarkCheck className="h-3 w-3" /> Aced
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1">
                                    <Globe2 className="h-3 w-3" /> {item.region || "Global"}
                                </span>
                                <span className="hidden sm:inline text-white/50">·</span>
                                <span className="hidden sm:inline font-mono tabular-nums text-white/70">№ {String(idx + 1).padStart(2, "0")}</span>
                            </div>
                        </div>

                        {!revealed ? (
                            <>
                                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/75 mb-1">Flash guess · +2 XP</div>
                                <div className={`text-xs sm:text-sm ${theme.accent} mb-3`}>🤔 {curiousHook(item)}</div>
                                <h2 className="font-heading font-bold text-3xl sm:text-5xl leading-[1.05] tracking-tight max-w-3xl drop-shadow-[0_3px_25px_rgba(0,0,0,0.35)]">
                                    {curiousQuestion(item)}
                                </h2>
                            </>
                        ) : (
                            <>
                                <h2 className="font-heading font-bold text-2xl sm:text-3xl leading-tight tracking-tight text-white/90 max-w-3xl">
                                    {item.title}
                                </h2>
                                {/* Big number band — the "cover shout" line */}
                                {number && (
                                    <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                        <span className="font-heading font-black text-6xl sm:text-8xl tabular-nums leading-none tracking-tight text-white drop-shadow-[0_6px_40px_rgba(0,0,0,0.5)]">
                                            {number}
                                        </span>
                                        {item.unit && item.unit !== "%" && (
                                            <span className={`text-lg font-medium ${theme.accent}`}>{item.unit}</span>
                                        )}
                                        {item.delta_pct !== undefined && item.delta_pct !== null && (
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                                                Number(item.delta_pct) >= 0 ? "bg-emerald-400/25 text-emerald-100 ring-1 ring-emerald-300/40" : "bg-rose-400/25 text-rose-100 ring-1 ring-rose-300/40"
                                            }`}>
                                                {Number(item.delta_pct) >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                                {Number(item.delta_pct) >= 0 ? "+" : ""}{Number(item.delta_pct).toFixed(1)}% {item.delta_label || "YoY"}
                                            </span>
                                        )}
                                        {pick && (
                                            pick === correctBucket ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/30 text-emerald-50 text-xs font-bold px-2.5 py-1 ring-1 ring-emerald-200/50">
                                                    Nailed it{awarded ? ` · +${awarded} XP` : ""}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-400/30 text-rose-50 text-xs font-bold px-2.5 py-1 ring-1 ring-rose-200/50">
                                                    Off by one bucket
                                                </span>
                                            )
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Celebration + wobble effects — mounted over the whole card */}

                {/* Celebration burst — three expanding teal rings + a "+2 XP"
                    float-up chip + confetti dots. Only mounts on a correct
                    answer, auto-cleans up ~1.6s later via `celebrating`. */}
                {celebrating && (
                    <div className="absolute inset-0 pointer-events-none z-30" data-testid="mr-flashcard-celebration">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            {[0, 0.15, 0.32].map((delay, i) => (
                                <span
                                    key={i}
                                    aria-hidden
                                    className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-24 w-24 rounded-full border-2 border-teal-400/60"
                                    style={{ animation: `mr-burst-ring 1200ms ${delay}s ease-out forwards` }}
                                />
                            ))}
                        </div>
                        {/* Floating confetti dots — scattered across the top */}
                        {[
                            { l: "12%", t: "18%", c: "#2EB4AC", d: 0.05 },
                            { l: "28%", t: "10%", c: "#5FE2DA", d: 0.15 },
                            { l: "48%", t: "8%",  c: "#F59E0B", d: 0.05 },
                            { l: "68%", t: "12%", c: "#EC4899", d: 0.2 },
                            { l: "82%", t: "18%", c: "#2EB4AC", d: 0.1 },
                            { l: "22%", t: "38%", c: "#5FE2DA", d: 0.28 },
                            { l: "74%", t: "42%", c: "#F59E0B", d: 0.3 },
                        ].map((p, i) => (
                            <span
                                key={i}
                                aria-hidden
                                className="absolute h-2 w-2 rounded-full"
                                style={{
                                    left: p.l,
                                    top: p.t,
                                    backgroundColor: p.c,
                                    animation: `mr-burst-confetti 1300ms ${p.d}s ease-out forwards`,
                                }}
                            />
                        ))}
                        {/* Central "+2 XP" chip that floats up + fades */}
                        <span
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-sm font-bold text-teal-800 border border-teal-200 shadow-lg"
                            style={{ animation: "mr-burst-chip 1400ms 0.1s ease-out forwards" }}
                        >
                            <Sparkles className="h-4 w-4 text-teal-500" />
                            {awarded > 0 ? `+${awarded} XP` : "Correct!"}
                        </span>
                        <style>{`
                            @keyframes mr-burst-ring {
                                0% { transform: translate(-50%,-50%) scale(0.4); opacity: 0.8; border-width: 3px; }
                                80% { opacity: 0.15; }
                                100% { transform: translate(-50%,-50%) scale(3.2); opacity: 0; border-width: 1px; }
                            }
                            @keyframes mr-burst-confetti {
                                0% { transform: translateY(0) scale(1); opacity: 0; }
                                15% { opacity: 1; }
                                100% { transform: translateY(-80px) scale(0.6); opacity: 0; }
                            }
                            @keyframes mr-burst-chip {
                                0% { transform: translate(-50%,-50%) scale(0.4); opacity: 0; }
                                20% { transform: translate(-50%,-50%) scale(1.15); opacity: 1; }
                                60% { transform: translate(-50%,calc(-50% - 24px)) scale(1); opacity: 1; }
                                100% { transform: translate(-50%,calc(-50% - 60px)) scale(0.9); opacity: 0; }
                            }
                            @keyframes mr-wobble {
                                0%, 100% { transform: translateX(0); }
                                20% { transform: translateX(-10px); }
                                40% { transform: translateX(10px); }
                                60% { transform: translateX(-6px); }
                                80% { transform: translateX(6px); }
                            }
                            @keyframes mr-puff-dot {
                                0% { transform: translate(-50%,-50%) scale(0.3); opacity: 0; }
                                20% { opacity: 0.9; }
                                100% { transform: translate(calc(-50% + var(--dx,0)), calc(-50% + var(--dy,0))) scale(0.5); opacity: 0; }
                            }
                        `}</style>
                    </div>
                )}

                {/* Wrong-answer feedback used to render a small on-card puff;
                    it now shows as a viewport-level modal below the article
                    (see WrongAnswerModal). The card just wobbles briefly. */}

                <div
                    ref={bodyRef}
                    className="relative p-6 sm:p-8"
                    style={{ transition: "transform 300ms cubic-bezier(0.2, 0.9, 0.3, 1)", willChange: "transform" }}
                >
                    {/* Front — buckets. Back — blurb + chart + actions. The
                        header (category / question / big number) already
                        renders inside the magazine hero above. */}
                    {!revealed ? (
                        <div>
                            <p className="text-xs text-slate-400 mb-4">Pick a range to reveal the actual figure.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                {buckets.map((b) => (
                                    <button
                                        key={b.key}
                                        onClick={() => answer(b.key)}
                                        className="group h-16 rounded-xl border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 hover:shadow-[0_10px_25px_-15px_rgba(46,180,172,0.55)] text-slate-800 text-sm font-semibold transition-all hover:-translate-y-0.5"
                                        data-testid={`mr-flashcard-guess-${b.key}`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                            {/* Source badge — reads calmer than the previous big Reveal CTA */}
                            <div className="mt-5 flex items-center gap-2 text-[11px] text-slate-500">
                                <ExternalLink className="h-3 w-3" /> Source: {item.source}
                            </div>
                        </div>
                    ) : (
                        <div className="animate-[mr-share-pop_240ms_ease-out]" data-testid="mr-flashcard-reveal">
                            {/* Blurb */}
                            <p className="text-slate-700 leading-relaxed max-w-2xl">{item.blurb}</p>

                            {/* Micro-comparison */}
                            {(() => {
                                const cmp = microComparison(item);
                                if (!cmp) return null;
                                return (
                                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                                        <Sparkles className="h-3.5 w-3.5 text-teal-500" /> {cmp}
                                    </div>
                                );
                            })()}

                            {/* Compact chart if series exists */}
                            {hasSeries && (
                                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-3 h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={item.series.points.map((p) => ({ label: String(p.label), value: Number(p.value) }))} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
                                            <defs>
                                                <linearGradient id={`mr-fc-fill-${item.id}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.45} />
                                                    <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                                            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} width={40} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                            <Area type="monotone" dataKey="value" stroke="#1F7F79" strokeWidth={2} fill={`url(#mr-fc-fill-${item.id})`} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Source + timestamp */}
                            <div className="mt-4 flex items-center justify-between gap-2 text-[11px]">
                                <a
                                    href={item.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 font-medium truncate"
                                    data-testid="mr-flashcard-source"
                                >
                                    <ExternalLink className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{item.source}</span>
                                </a>
                                {item.ingested_at && (
                                    <span className="inline-flex items-center gap-1 text-slate-400 shrink-0" title={new Date(item.ingested_at).toLocaleString("en-IN")}>
                                        <Clock className="h-3 w-3" /> {relTime(item.ingested_at)}
                                    </span>
                                )}
                            </div>

                            {/* Action row — react/note/share/save + See full detail */}
                            <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                                <ReactionRail
                                    counts={item.reaction_counts}
                                    mine={item.my_reactions}
                                    onReact={(e) => onReact(item, e)}
                                    busy={reactingId === item.id}
                                />
                                <div className="flex items-center gap-1">
                                    <button onClick={() => onOpenNotes(item)} className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center relative" title="Community notes">
                                        <MessageCircle className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => onShare(item)} className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-flex items-center justify-center" title="Share">
                                        <Share2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => onToggleSave(item)}
                                        disabled={savingId === item.id}
                                        className={`h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors ${
                                            savedIds.includes(item.id) ? "bg-teal-50 text-teal-700" : "hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                                        }`}
                                        title={savedIds.includes(item.id) ? "Saved" : "Save"}
                                    >
                                        {savedIds.includes(item.id) ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                                    </button>
                                    <button
                                        onClick={() => onOpenDetail(item)}
                                        data-testid="mr-flashcard-full-breakdown"
                                        className="ml-1 relative inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] hover:from-[#25908A] hover:to-[#0F3D3B] text-white text-sm font-bold shadow-[0_10px_30px_-10px_rgba(46,180,172,0.7)] hover:-translate-y-0.5 transition-all group"
                                    >
                                        <span aria-hidden className="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 blur transition-opacity" />
                                        <BarChart3 className="relative h-4 w-4" />
                                        <span className="relative">View full report</span>
                                        <span aria-hidden className="relative">→</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </article>

            {/* Prev / Next controls */}
            <div className="mt-5 flex items-center justify-between">
                <button
                    onClick={prev}
                    disabled={idx === 0}
                    className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium text-slate-700"
                    data-testid="mr-flashcard-prev"
                >
                    <span aria-hidden>←</span> Prev
                </button>
                <div className="text-[11px] text-slate-400 hidden sm:block">
                    ← / → to navigate · <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 font-mono text-[10px]">1</kbd> <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 font-mono text-[10px]">2</kbd> <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 font-mono text-[10px]">3</kbd> to answer
                </div>
                <button
                    onClick={next}
                    disabled={idx >= total - 1}
                    className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] hover:from-[#25908A] hover:to-[#0F3D3B] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_10px_30px_-10px_rgba(46,180,172,0.6)]"
                    data-testid="mr-flashcard-next"
                >
                    Next <span aria-hidden>→</span>
                </button>
            </div>

            {/* Wrong-answer viewport modal — pops centred on screen after a
                miss, showing the correct figure + encouragement + a Next CTA. */}
            {revealed && pick && pick !== correctBucket && (
                <WrongAnswerModal
                    item={item}
                    number={number}
                    onClose={() => { /* modal auto-closes via its Continue btn */ }}
                    onNext={() => next()}
                    isLast={idx >= total - 1}
                />
            )}
        </div>
    );
};

// ------- Wrong-answer viewport modal — replaces the on-card puff overlay.
// Renders centred, portal-mounted, with a rose duotone hero, the actual
// figure prominently, and "Continue" / "Next card" CTAs.
const WrongAnswerModal = ({ item, number, onNext, isLast }) => {
    const [dismissed, setDismissed] = useState(false);
    const dialogRef = useRef(null);

    // Auto-focus the primary CTA so Enter dismisses immediately.
    useEffect(() => {
        const el = dialogRef.current?.querySelector('[data-testid="mr-wrong-next-btn"]');
        el?.focus();
    }, []);

    // Esc to dismiss.
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") setDismissed(true); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, []);

    if (dismissed) return null;

    const goNext = () => {
        setDismissed(true);
        if (!isLast) setTimeout(() => onNext?.(), 120);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-950/45 backdrop-blur-md animate-[mr-share-pop_180ms_ease-out]"
            data-testid="mr-wrong-modal"
        >
            <div ref={dialogRef} className="relative w-full max-w-md">
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_30px_90px_-20px_rgba(244,63,94,0.55)] border border-rose-100 animate-[mr-share-pop_240ms_ease-out]">
                    {/* Rose-duotone hero band */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#7F1D1D] via-[#BE123C] to-[#F43F5E] text-white p-6">
                        <div aria-hidden className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/15 blur-3xl" />
                        <div
                            aria-hidden
                            className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
                            style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "12px 12px" }}
                        />
                        <div className="relative flex items-start justify-between gap-3">
                            <div>
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.22em] font-bold">
                                    🎯 Not quite
                                </div>
                                <h3 className="mt-2 font-heading font-bold text-2xl leading-tight">
                                    Off by one bucket.
                                </h3>
                                <p className="mt-1 text-sm text-rose-50/90">The real number caught most people out too.</p>
                            </div>
                            <button
                                onClick={() => setDismissed(true)}
                                aria-label="Close"
                                data-testid="mr-wrong-close-btn"
                                className="shrink-0 h-8 w-8 rounded-full bg-white/15 hover:bg-white/30 inline-flex items-center justify-center transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Body — the correct figure */}
                    <div className="p-6">
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">The actual figure</div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-heading font-black text-5xl sm:text-6xl text-slate-900 tabular-nums leading-none tracking-tight">
                                {number}
                            </span>
                            {item.unit && item.unit !== "%" && (
                                <span className="text-base text-slate-500 font-medium">{item.unit}</span>
                            )}
                            {item.delta_pct !== undefined && item.delta_pct !== null && (
                                <DeltaPill pct={item.delta_pct} label={item.delta_label} />
                            )}
                        </div>
                        <div className="mt-3 text-sm text-slate-700 leading-relaxed">{item.blurb}</div>
                        <div className="mt-3 text-[11px] text-slate-500 inline-flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> {item.source}
                        </div>

                        {/* CTA row */}
                        <div className="mt-5 flex gap-2">
                            <button
                                onClick={() => setDismissed(true)}
                                className="flex-1 h-11 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium"
                                data-testid="mr-wrong-continue-btn"
                            >
                                See the details
                            </button>
                            {!isLast && (
                                <button
                                    onClick={goNext}
                                    className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] hover:from-[#25908A] hover:to-[#0F3D3B] text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 shadow-[0_8px_20px_-8px_rgba(46,180,172,0.6)]"
                                    data-testid="mr-wrong-next-btn"
                                >
                                    Next card <span aria-hidden>→</span>
                                </button>
                            )}
                        </div>
                        <div className="mt-2 text-[10px] text-slate-400 text-center">
                            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px]">Esc</kbd> to dismiss
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};


// ------- Main page -------
const MarketResearch = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [regions, setRegions] = useState([]);
    const [savedIds, setSavedIds] = useState([]);
    const [noteCounts, setNoteCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // filters
    const [kind, setKind] = useState("");
    const [category, setCategory] = useState("");
    const [region, setRegion] = useState("");
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("recent");
    const [showSavedOnly, setShowSavedOnly] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const [openNoteItem, setOpenNoteItem] = useState(null);
    const [openDetailItem, setOpenDetailItem] = useState(null);
    const [openShareItem, setOpenShareItem] = useState(null);
    // Flashcard vs grid view — flashcard is the default landing UX; grid
    // is a "Show all" alt view for scanning at a glance. Persisted in
    // localStorage so a user's last choice sticks between sessions.
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === "undefined") return "flashcard";
        return window.localStorage.getItem("mr:viewMode") || "flashcard";
    });
    useEffect(() => {
        try { window.localStorage.setItem("mr:viewMode", viewMode); } catch { /* private mode */ }
    }, [viewMode]);
    // Enforce role gating — non-admins can only see flashcard mode.
    useEffect(() => {
        if (user && user.role !== "super_admin" && viewMode !== "flashcard") {
            setViewMode("flashcard");
        }
    }, [user, viewMode]);
    const [savingId, setSavingId] = useState("");
    const [reactingId, setReactingId] = useState("");
    const [lastFetchedAt, setLastFetchedAt] = useState("");
    // Force a re-render every 60s so the "Updated Xm ago" pill keeps ticking
    // without polling the backend. Cheap; no visible impact.
    const [, setTick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setTick((n) => n + 1), 60_000);
        return () => clearInterval(t);
    }, []);

    // Fetch feed with current filters
    const fetchFeed = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (kind) params.set("kind", kind);
            if (category) params.set("category", category);
            if (region) params.set("region", region);
            if (q) params.set("q", q);
            params.set("sort", sort);
            params.set("limit", "80");
            const [feedRes, savedRes] = await Promise.all([
                api.get(`/research/feed?${params.toString()}`),
                api.get("/research/saved/me").catch(() => ({ data: { saved_ids: [] } })),
            ]);
            const data = feedRes.data || {};
            setItems(data.items || []);
            setCategories(data.categories || []);
            setRegions(data.regions || []);
            setLastFetchedAt(data.last_fetched_at || "");
            setSavedIds(savedRes.data?.saved_ids || []);
        } catch (e) {
            toast.error("Couldn't load market research");
        } finally {
            setLoading(false);
        }
    }, [kind, category, region, q, sort]);

    useEffect(() => { fetchFeed(); }, [fetchFeed]);

    // Fetch note counts lazily (one call per item) — we only care about the
    // count, not the full thread. Batched fire-and-forget.
    useEffect(() => {
        if (!items.length) return;
        let cancelled = false;
        (async () => {
            const results = await Promise.allSettled(
                items.slice(0, 30).map((it) => api.get(`/research/${it.id}/notes`))
            );
            if (cancelled) return;
            const counts = {};
            results.forEach((r, idx) => {
                if (r.status === "fulfilled") counts[items[idx].id] = (r.value.data || []).length;
            });
            setNoteCounts(counts);
        })();
        return () => { cancelled = true; };
    }, [items]);

    const filtered = useMemo(() => {
        let out = items;
        if (showSavedOnly) out = out.filter((it) => savedIds.includes(it.id));
        // Cards WITH a historical series float to the top so the visually
        // richest content leads the feed. Trending sort is left alone —
        // the user has explicitly asked for reaction-ranked order.
        if (sort !== "trending") {
            out = [...out].sort((a, b) => {
                const aHas = Array.isArray(a.series?.points) && a.series.points.length >= 2 ? 1 : 0;
                const bHas = Array.isArray(b.series?.points) && b.series.points.length >= 2 ? 1 : 0;
                return bHas - aHas; // stable secondary order preserved from backend
            });
        }
        return out.map((it) => ({ ...it, note_count: noteCounts[it.id] || 0 }));
    }, [items, showSavedOnly, savedIds, noteCounts, sort]);

    // ----- actions -----
    const react = async (item, emoji) => {
        setReactingId(item.id);
        try {
            const r = await api.post(`/research/${item.id}/react`, { emoji });
            setItems((prev) => prev.map((x) => x.id === item.id
                ? { ...x, reaction_counts: r.data.reaction_counts, my_reactions: r.data.my_reactions }
                : x));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't react");
        } finally {
            setReactingId("");
        }
    };

    const toggleSave = async (item) => {
        setSavingId(item.id);
        const isSaved = savedIds.includes(item.id);
        try {
            if (isSaved) {
                await api.delete(`/research/${item.id}/save`);
                setSavedIds((prev) => prev.filter((id) => id !== item.id));
                toast.success("Removed from your board");
            } else {
                await api.post(`/research/${item.id}/save`);
                setSavedIds((prev) => [...prev, item.id]);
                // Actionable toast — a "View board" chip that jumps to the
                // grid view filtered to Saved-only, so the user immediately
                // sees where the "board" lives.
                toast.success("Saved · Added to your board", {
                    action: {
                        label: "View board",
                        onClick: () => {
                            setViewMode("grid");
                            setShowSavedOnly(true);
                            if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
                        },
                    },
                    duration: 5000,
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        } finally {
            setSavingId("");
        }
    };

    const refresh = async () => {
        if (user?.role !== "super_admin") return;
        setRefreshing(true);
        try {
            const r = await api.post("/research/refresh");
            toast.success(`Fetched ${r.data?.fetched || 0} reports (${r.data?.inserted || 0} new)`);
            await fetchFeed();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    const handleNoteCountChange = useCallback((itemId, n) => {
        setNoteCounts((prev) => ({ ...prev, [itemId]: n }));
    }, []);

    const clearFilters = () => {
        setKind(""); setCategory(""); setRegion(""); setQ(""); setSort("recent"); setShowSavedOnly(false);
    };

    const hasFilters = kind || category || region || q || showSavedOnly || sort !== "recent";

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="market-research-page">
            {/* Hero — mirrors the Learning Hub aesthetic: two-column dark
                panel with an animated conic border, futuristic grid layer,
                and the user-supplied coffee/charts photograph as a right
                column that fades into the dark left side. */}
            <div className="relative overflow-hidden rounded-2xl bg-[#070A0E] text-white shadow-[0_20px_60px_-15px_rgba(46,180,172,0.35)] learning-hero mb-6">
                <div className="absolute inset-0 rounded-2xl learning-hero-border pointer-events-none" aria-hidden />
                <div className="relative m-[1px] rounded-[15px] overflow-hidden bg-gradient-to-br from-[#070A0E] via-[#0B1117] to-[#0E1F1E]">
                    <div className="absolute inset-0 learning-hero-grid pointer-events-none" aria-hidden />
                    <div className="absolute inset-0 learning-hero-scan pointer-events-none" aria-hidden />
                    <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-[#2EB4AC]/30 blur-3xl pointer-events-none" />
                    <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-[#2EB4AC]/15 blur-2xl pointer-events-none" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 relative">
                        <div className="relative p-8 sm:p-10 lg:p-12 z-10">
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold bg-[#2EB4AC]/10 text-[#2EB4AC] border border-[#2EB4AC]/30 backdrop-blur">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2EB4AC] opacity-70" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2EB4AC]" />
                                </span>
                                Quiz &amp; Facts · Live
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-bold mt-4 tracking-tight leading-[1.05]">
                                Industry intelligence,
                                <span className="block bg-gradient-to-r from-[#2EB4AC] via-[#5FE2DA] to-[#2EB4AC] bg-clip-text text-transparent learning-hero-shimmer">
                                    delivered card by card.
                                </span>
                            </h2>
                            <p className="text-slate-300 mt-4 text-sm sm:text-base leading-relaxed max-w-lg">
                                Curated stats from public sources like DataReportal. Interactive flashcards to sharpen your read of the market — with XP for accurate guesses and a daily insight worth remembering.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                                <div className="inline-flex items-center gap-2 text-slate-400">
                                    <span className="h-1 w-1 rounded-full bg-[#2EB4AC]" />
                                    <span className="tabular-nums font-mono text-[#2EB4AC] font-bold text-base">{items.length}</span>
                                    <span>insights</span>
                                </div>
                                <div className="inline-flex items-center gap-2 text-slate-400">
                                    <span className="h-1 w-1 rounded-full bg-[#2EB4AC]" />
                                    <span className="tabular-nums font-mono text-[#2EB4AC] font-bold text-base">{savedIds.length}</span>
                                    <span>saved</span>
                                </div>
                                <div
                                    className="inline-flex items-center gap-2 text-slate-400"
                                    title={lastFetchedAt ? new Date(lastFetchedAt).toLocaleString("en-IN") : ""}
                                    data-testid="mr-hero-last-fetched"
                                >
                                    <Clock className="h-3 w-3 text-[#2EB4AC]" />
                                    <span>Updated {lastFetchedAt ? relTime(lastFetchedAt) : "—"}</span>
                                </div>
                                <button
                                    onClick={refresh}
                                    disabled={refreshing}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#2EB4AC] hover:text-[#5FE2DA] disabled:opacity-40 transition-colors"
                                    title="Refresh from DataReportal"
                                    data-testid="mr-refresh-btn"
                                    style={{ display: user?.role === "super_admin" ? undefined : "none" }}
                                >
                                    {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                    {refreshing ? "Refreshing…" : "Refresh"}
                                </button>
                            </div>
                        </div>
                        <div className="relative min-h-[220px] lg:min-h-[320px] overflow-hidden hidden lg:block">
                            {/* Pure-CSS decorative panel — no external images so it
                                never renders as a broken image / alt-text in prod.
                                Layers: base gradient, radial glow, chart-line SVG,
                                animated grid, and a soft fade back into the left column. */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#0B1D1B] via-[#0A1517] to-[#070A0E]" />
                            <div className="absolute inset-0" style={{
                                background: "radial-gradient(1200px 400px at 80% 20%, rgba(46,180,172,0.35), transparent 60%), radial-gradient(600px 300px at 20% 90%, rgba(46,180,172,0.18), transparent 60%)",
                            }} />
                            <svg
                                aria-hidden
                                viewBox="0 0 400 320"
                                preserveAspectRatio="none"
                                className="absolute inset-0 h-full w-full opacity-70"
                            >
                                <defs>
                                    <linearGradient id="mr-hero-line" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity="0" />
                                        <stop offset="50%" stopColor="#5FE2DA" stopOpacity="1" />
                                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="mr-hero-fill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity="0.28" />
                                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M0 240 L40 220 L80 232 L120 200 L160 210 L200 170 L240 190 L280 140 L320 158 L360 108 L400 130 L400 320 L0 320 Z"
                                    fill="url(#mr-hero-fill)"
                                />
                                <path
                                    d="M0 240 L40 220 L80 232 L120 200 L160 210 L200 170 L240 190 L280 140 L320 158 L360 108 L400 130"
                                    fill="none"
                                    stroke="url(#mr-hero-line)"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {[
                                    [40, 220], [120, 200], [200, 170], [280, 140], [360, 108],
                                ].map(([cx, cy]) => (
                                    <g key={`${cx}-${cy}`}>
                                        <circle cx={cx} cy={cy} r="6" fill="#2EB4AC" opacity="0.25" />
                                        <circle cx={cx} cy={cy} r="3" fill="#5FE2DA" />
                                    </g>
                                ))}
                            </svg>
                            <div className="absolute inset-0 learning-hero-grid opacity-40" />
                            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#070A0E] via-[#070A0E]/60 to-transparent" />
                        </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#2EB4AC]/70 to-transparent" />
                </div>
            </div>

            {/* Filter bar */}
            <div className="mb-6 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search stats & reports"
                            className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none"
                            data-testid="mr-search-input"
                        />
                    </div>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-teal-400 outline-none"
                        data-testid="mr-sort-select"
                    >
                        <option value="recent">Newest first</option>
                        <option value="trending">🔥 Trending</option>
                    </select>
                    <button
                        onClick={() => setShowFilters((v) => !v)}
                        className={`h-10 px-3 rounded-xl border text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                            showFilters ? "bg-teal-50 border-teal-200 text-teal-800" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid="mr-filters-toggle"
                    >
                        <Filter className="h-4 w-4" /> Filters
                    </button>
                    <button
                        onClick={() => setShowSavedOnly((v) => !v)}
                        className={`h-10 px-3 rounded-xl border text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                            showSavedOnly ? "bg-teal-50 border-teal-200 text-teal-800" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid="mr-saved-toggle"
                    >
                        <Bookmark className="h-4 w-4" /> Saved ({savedIds.length})
                    </button>
                    {hasFilters && (
                        <button
                            onClick={clearFilters}
                            className="h-10 px-3 rounded-xl text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                            data-testid="mr-clear-filters"
                        >
                            <X className="h-4 w-4" /> Clear
                        </button>
                    )}
                </div>

                {/* Category quick-chip row — mirrors the News topic strip.
                    All-visible tabs so users can pivot without opening the
                    Filters drawer. */}
                {categories.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="mr-topic-chips">
                        <button
                            onClick={() => setCategory("")}
                            className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${
                                !category ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                            }`}
                            data-testid="mr-topic-chip-all"
                        >All</button>
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c === category ? "" : c)}
                                className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${
                                    category === c
                                        ? "bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] text-white shadow-sm"
                                        : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                                }`}
                                data-testid={`mr-topic-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            >{c}</button>
                        ))}
                    </div>
                )}

                {showFilters && (
                    <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-400 mr-1">Kind</div>
                        {[
                            { v: "", label: "All" },
                            { v: "stat", label: "Stats" },
                            { v: "report", label: "Reports" },
                        ].map((c) => (
                            <button
                                key={c.v}
                                onClick={() => setKind(c.v)}
                                className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${
                                    kind === c.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                }`}
                                data-testid={`mr-filter-kind-${c.v || "all"}`}
                            >
                                {c.label}
                            </button>
                        ))}
                        <div className="w-px h-6 bg-slate-200 mx-1" />
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-400 mr-1">Region</div>
                        <button
                            onClick={() => setRegion("")}
                            className={`h-8 px-3 rounded-full text-xs font-medium ${!region ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                        >All</button>
                        {regions.map((r) => (
                            <button
                                key={r}
                                onClick={() => setRegion(r === region ? "" : r)}
                                className={`h-8 px-3 rounded-full text-xs font-medium ${region === r ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                            >{r}</button>
                        ))}
                    </div>
                )}
            </div>

            {/* Stat of the Day — daily engagement hook. Rendered above the
                grid so it's the first thing users see when they land here. */}
            {!loading && items.length > 0 && (
                <StatOfTheDay items={items} onOpenDetail={setOpenDetailItem} />
            )}

            {/* Feed */}
            {loading ? (
                <div className="flex items-center justify-center py-24 text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading market data…
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20">
                    <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <div className="text-slate-600 font-medium">
                        {showSavedOnly
                            ? "You haven't saved anything yet"
                            : kind === "report"
                                ? "No reports scraped yet"
                                : "No results match your filters"}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                        {showSavedOnly
                            ? "Tap the bookmark on any card to save it here."
                            : kind === "report"
                                ? (user?.role === "super_admin"
                                    ? "Tap \u201CRefresh from DataReportal\u201D above to pull the latest reports."
                                    : "Ask an admin to refresh from DataReportal — new reports drop weekly.")
                                : "Try clearing filters or the search box."}
                    </div>
                </div>
            ) : viewMode === "flashcard" ? (
                <FlashcardMode
                    items={filtered.filter((i) => i.kind === "stat")}
                    savedIds={savedIds}
                    savingId={savingId}
                    reactingId={reactingId}
                    canShowGrid={user?.role === "super_admin"}
                    onSwitchToGrid={() => setViewMode("grid")}
                    onOpenBoard={() => { setViewMode("grid"); setShowSavedOnly(true); }}
                    onReact={react}
                    onToggleSave={toggleSave}
                    onOpenNotes={setOpenNoteItem}
                    onShare={setOpenShareItem}
                    onOpenDetail={setOpenDetailItem}
                />
            ) : (
                <>
                    <div className="mb-4 flex items-center justify-between">
                        <div className="text-sm text-slate-500">
                            <span className="font-bold text-slate-900">{filtered.length}</span> insights · grid view
                        </div>
                        <button
                            onClick={() => setViewMode("flashcard")}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-[#2EB4AC] to-[#1F7F79] text-white text-xs font-semibold shadow-[0_8px_20px_-8px_rgba(46,180,172,0.55)]"
                            data-testid="mr-back-to-flashcard"
                        >
                            <Sparkles className="h-3.5 w-3.5" /> Flashcard mode
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                        {filtered.map((item) =>
                            item.kind === "report" ? (
                                <ReportCard
                                    key={item.id}
                                    item={item}
                                    saved={savedIds.includes(item.id)}
                                    onToggleSave={toggleSave}
                                    onReact={react}
                                    onOpenNotes={setOpenNoteItem}
                                    onShare={setOpenShareItem}
                                    savingId={savingId}
                                    reactingId={reactingId}
                                />
                            ) : (
                                <StatCard
                                    key={item.id}
                                    item={item}
                                    saved={savedIds.includes(item.id)}
                                    onToggleSave={toggleSave}
                                    onReact={react}
                                    onOpenNotes={setOpenNoteItem}
                                    onOpenDetail={setOpenDetailItem}
                                    onShare={setOpenShareItem}
                                    savingId={savingId}
                                    reactingId={reactingId}
                                />
                            )
                        )}
                    </div>
                </>
            )}

            {openNoteItem && (
                <NoteThread
                    item={openNoteItem}
                    onClose={() => setOpenNoteItem(null)}
                    onCountChange={handleNoteCountChange}
                />
            )}
            {openShareItem && (
                <ShareDialog item={openShareItem} onClose={() => setOpenShareItem(null)} />
            )}
            {openDetailItem && (
                <StatDetailDialog
                    item={{ ...openDetailItem, ...(items.find((x) => x.id === openDetailItem.id) || {}) }}
                    onClose={() => setOpenDetailItem(null)}
                    onOpenNotes={setOpenNoteItem}
                    onShare={setOpenShareItem}
                    onToggleSave={toggleSave}
                    onReact={react}
                    saved={savedIds.includes(openDetailItem.id)}
                />
            )}
        </div>
    );
};

export default MarketResearch;
